import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { Spinner } from './Spinner'
import { cx } from './cx'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'outline-brand'
  | 'link'
  | 'danger'
  | 'danger-outline'
  | 'purple'
  | 'outline-purple'
  | 'warning'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl'

/**
 * The variant+size → className recipe `Button` renders with, exported so a
 * caller that can't use the `<button>` element itself (e.g. `HomePage`'s
 * "Choose files or BOM" `<label>` — a native `<button>` can't wrap a file
 * input) still sources its classes from here instead of retyping the
 * `btn btn-<variant> btn-<size>` string by hand, where it could silently
 * drift from this component's own render.
 */
export function buttonClassName(
  {
    variant = 'primary',
    size = 'md',
  }: { readonly variant?: ButtonVariant; readonly size?: ButtonSize } = {},
  className?: string,
): string {
  return cx('btn', `btn-${variant}`, `btn-${size}`, className)
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant
  readonly size?: ButtonSize
  /** Shows a spinner and disables the button. Passing this at all (even
   * `false`) reserves the spinner's slot (plus a mirroring blank slot on
   * the other side, so the label stays centered) — so a button that ever
   * uses `loading` doesn't resize when it toggles. A button that never
   * passes this prop gets no reserved space at all. */
  readonly loading?: boolean
  readonly children: ReactNode
  /** React 19 supports `ref` as an ordinary prop on function components
   * (no `forwardRef` needed) — declared explicitly here so callers that
   * need the underlying `<button>` node (e.g. an anchored popover trigger,
   * `useAnchoredPopover`) can get it, same as any native element. */
  readonly ref?: Ref<HTMLButtonElement>
}

/**
 * The one Button primitive every screen composes (components.md "Button" —
 * primary/secondary/outline/link/danger + a purple variant for RFQ/quote
 * actions, sm(24)/md(32)/lg(40)/xl(46), with default/hover/active/focus/
 * disabled/loading states). Native `<button>` so keyboard operability and
 * the shared `:focus-visible` ring (global.css) come for free.
 *
 * `outline-brand` and `danger-outline` (UID-130 follow-up) are ghost/outline
 * variants — white background, colored border + text, no solid fill — for
 * header-row trigger actions where a solid `primary`/`danger` fill would be
 * too visually heavy (e.g. Cart's "Add a product" / "Clear cart" triggers; the
 * actual destructive confirmation stays solid `danger` in its dialog).
 */
export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  className,
  children,
  ref,
  ...rest
}: ButtonProps) {
  const classes = buttonClassName({ variant, size }, className)
  const supportsLoading = loading !== undefined
  const isLoading = loading === true

  return (
    <button
      ref={ref}
      type="button"
      className={classes}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...rest}
    >
      {supportsLoading && (
        <Spinner
          size="sm"
          aria-hidden="true"
          className={isLoading ? undefined : 'btn-spinner-idle'}
        />
      )}
      <span className={cx('btn-content', isLoading && 'btn-label-loading')}>{children}</span>
      {supportsLoading && <span className="btn-spinner-spacer" aria-hidden="true" />}
    </button>
  )
}
